import { Material } from '@cutr/constants/cutlist';
import cn from 'classnames';
import React from 'react';

import {
  getCoreMaterials,
  getCoreMaterialsForGluingAndPressing,
  getEdgebandingMaterials,
  getHPLMaterials,
} from '@/api/materials';
import {
  DEFAULT_MATERIAL_LIST_SIZE,
  MaterialSearchType,
  useMaterialSearch,
} from '@/api/materialSearch';
import {
  selectHasMaterials,
  useCutlistState,
  useUsedBandingMaterials,
  useUsedCoreMaterials,
  useUsedHplMaterials,
} from '@/api/store';
import { onFocusSelect } from '@/hooks';
import { Icon, Search } from '@/primitives/Icons';
import { Input } from '@/primitives/Input';
import { useCurrentFeatures, useTicker } from '@/theme';
import { useDebounce } from '@/utils/hooks';

import styles from './styles.module.css';

const sortByArticleCode = (materials: Material[]) =>
  [...materials].sort((a, b) =>
    a.articleCode.localeCompare(b.articleCode, undefined, { numeric: true })
  );

interface AutocompleteProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value?: Material['articleCode'];
  placeholder: string;
  id: string;
  materialSearchType: MaterialSearchType;
  inputClass?: string;
  hasSelection?: boolean;
  onResults: (results: Material[], isDefaultList: boolean) => void;
}

export const Autocomplete = ({
  placeholder,
  value,
  id,
  materialSearchType,
  inputClass,
  hasSelection = false,
  onResults,
  ...props
}: AutocompleteProps) => {
  const [val, setVal] = React.useState<Material['articleCode']>(
    (value as string) || ''
  );
  const [sendTrackBlur, setSendTrackBlur] = React.useState(false);
  const source = useTicker();
  const { sortMaterialsByArticleCode } = useCurrentFeatures();

  const debouncedVal = useDebounce<string>(val, 100, value);
  let searchResults = useMaterialSearch(debouncedVal, materialSearchType);
  const hasMaterials = useCutlistState(selectHasMaterials);

  const coreMaterials = useUsedCoreMaterials();
  const hplMaterials = useUsedHplMaterials();
  const edgebandingMaterials = useUsedBandingMaterials();

  const recentMaterials =
    materialSearchType === 'edgeband'
      ? edgebandingMaterials
      : materialSearchType === 'hpl'
        ? hplMaterials
        : coreMaterials;

  const defaultMaterials = React.useMemo(() => {
    if (materialSearchType === 'edgeband')
      return getEdgebandingMaterials().slice(0, DEFAULT_MATERIAL_LIST_SIZE);
    if (materialSearchType === 'hpl')
      return getHPLMaterials().slice(0, DEFAULT_MATERIAL_LIST_SIZE);
    if (materialSearchType === 'coreForGluingAndPressing')
      return getCoreMaterialsForGluingAndPressing().slice(
        0,
        DEFAULT_MATERIAL_LIST_SIZE
      );
    return getCoreMaterials().slice(0, DEFAULT_MATERIAL_LIST_SIZE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialSearchType, hasMaterials]);

  // In the case where a customer types in their search, accidentally clicks out of
  // the field and then back in, we don't want to treat that as a search failure.
  // This gives a two second buffer for them to click back in before we track
  // the blur as a failed search.
  React.useEffect(() => {
    if (sendTrackBlur) {
      const cancellation = setTimeout(() => {
        window.analytics.track('Searched for material', {
          searchInput: val,
          materialSearchType: materialSearchType,
          success: false,
          ticker: source,
          resultCount: searchResults.length,
        });
        setSendTrackBlur(false);
      }, 2000);

      return () => {
        clearTimeout(cancellation);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendTrackBlur]);

  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVal(String(e.target.value));
  };

  React.useEffect(() => {
    if (!debouncedVal || (hasSelection && debouncedVal === value)) {
      const list = recentMaterials.length ? recentMaterials : defaultMaterials;
      onResults(
        sortMaterialsByArticleCode ? sortByArticleCode(list) : list,
        true
      );
    } else {
      onResults(searchResults, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedVal, hasMaterials]);

  return (
    <div className={styles.autocomplete}>
      <Input
        id={`autocomplete-input-${id}`}
        icon={<Icon icon={<Search />} />}
        placeholder={placeholder}
        value={val}
        autoComplete="off"
        aria-autocomplete="both"
        aria-controls={`autocomplete-${id}`}
        aria-haspopup="listbox"
        role="combobox"
        className={cn(inputClass, 'w-border')}
        onInput={onInput}
        onFocus={(e) => {
          onFocusSelect(e);
          setSendTrackBlur(false);
        }}
        {...props}
      />
    </div>
  );
};
